
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import AiInterviewProfileCard from "@/components/candidate/interviews/ai/AiInterviewProfileCard.vue";
import AiInterviewDetailsCard from "@/components/candidate/interviews/ai/AiInterviewDetailsCard.vue";
import {
  GET_USER_DETAILS,
  USER_PROFILE_BY_ID
} from "@/store/modules/auth/constants";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import {
  AI_INTERVIEW_INVITES,
  INIT_AI_INTERVIEW_INVITES
} from "@/store/modules/recruiter_ai_interviews/constants";
import { get_latest_interview_report } from "@/store/apis/ml_interview";
import { get_job_by_id } from "@/store/apis/common";

export default Vue.extend({
  name: "CandidateAiInterview",
  components: {
    AiInterviewProfileCard,
    AiInterviewDetailsCard
  },
  data() {
    return {
      coverImage: require("@/assets/images/interviews/ai_interview_cover.png"),
      interview: {
        title: "Interview with AI Interviewer",
        date: "2025-05-20T22:30:00+04:00",
        formattedDate: "20th May, 2025",
        formattedTime: "10:30pm United Arab Emirates (GMT+4)",
        role: "Software Engineer",
        format: "Initial AI Interview",
        duration: "30 Minutes",
        transcriptVia: "Microsoft Teams",
        link: "https://teams.microsoft.com/l/meetup-join/19%3ameeting_NzQ2YjA1MjEtY2FhZi00Y2YwLWI2NjQtODk3MzE5YjY4Y2Qx%40thread.v2/0",
        validationAreas: [
          "HTML",
          "CSS",
          "SQL",
          "Python",
          "JavaScript",
          "C#",
          "Ruby",
          "Go"
        ]
      },
      aiInterviewCompleted: false
      ,
      resolvedJobTitle: "",
      candidateProfile: null as any
    };
  },
  computed: {
    ...mapGetters("auth", {
      user: GET_USER_DETAILS
    }),
    ...mapGetters("recruiter_ai_interviews", {
      aiInvites: AI_INTERVIEW_INVITES
    }),
    candidateName(this: any): string {
      if (!this.user?.first_name) return "Ahmad Al-Mansouri";
      return `${this.user.first_name} ${this.user.last_name || ""}`.trim();
    },
    candidateRole(this: any): string {
      const ownRole =
        this.extractCandidateOwnRole(this.candidateProfile) ||
        this.extractCandidateOwnRole(this.user?.profile || this.user?.user_profile || {});
      if (ownRole) return ownRole;
      return (
        this.resolvedJobTitle ||
        this.candidateInvite?.job_title ||
        this.candidateInvite?.job_role ||
        this.candidateInvite?.candidate_role ||
        "Candidate"
      );
    },
    detailsTitle(this: any): string {
      return "Interview Details";
    },
    resumeLabel(this: any): string {
      return "View my resume";
    },
    joinLabel(this: any): string {
      if (this.aiInterviewCompleted) return "AI Interview Completed";
      if (this.isInterviewExpired) return "Interview Expired";
      return this.candidateInvite ? "Join AI Interview" : "Join";
    },
    isInterviewExpired(this: any): boolean {
      const scheduledAt = String(this.candidateInvite?.scheduled_at || "").trim();
      if (!scheduledAt) return false;
      const scheduledTs = new Date(scheduledAt).getTime();
      if (!Number.isFinite(scheduledTs)) return false;
      return Date.now() > scheduledTs;
    },
    joinDisabled(this: any): boolean {
      return !this.candidateInvite || this.aiInterviewCompleted || this.isInterviewExpired;
    },
    candidateInvite(this: any): any {
      const candidateId = this.user?.id;
      if (!candidateId) return null;
      const inviteId = String(this.$route?.params?.inviteId || "").trim();
      const validInvites = (this.aiInvites || []).filter((i: any) => {
        if (i?.candidate_id !== candidateId) return false;
        if (inviteId) return String(i?.id || "") === inviteId;
        if (i?.status !== "scheduled") return false;
        if (!i?.scheduled_at) return false;
        return !isNaN(new Date(i.scheduled_at).getTime());
      });
      return validInvites[0] ?? null;
    },
    validationAreasLabel(this: any): string {
      return "Critical Validation Areas";
    },
    resumeUrl(this: any): string {
      return (
        this.user?.resume_uri ||
        this.user?.user_data?.resume_uri ||
        this.user?.profile?.resume_uri ||
        ""
      );
    }
  },
  methods: {
    ...mapActions("auth", {
      fetchProfileById: USER_PROFILE_BY_ID
    }),
    ...mapMutations({
      set_root_notification: ROOT_NOTIFICATION
    }),
    extractCandidateOwnRole(this: any, profileWrapper: any): string {
      const profile = profileWrapper?.profile || profileWrapper || {};
      const workHistory = profile?.work_history_detailed;
      if (!workHistory) return "";
      const entries = Object.values(workHistory || {}) as any[];
      for (const entry of entries) {
        const role = String(entry?.job_title || "").trim();
        if (role) return role;
      }
      return "";
    },
    onViewResume(this: any) {
      if (!this.resumeUrl) {
        this.set_root_notification("Resume is not available.");
        return;
      }
      window.open(this.resumeUrl, "_blank");
    },
    onJoinInterview(this: any) {
      if (this.joinDisabled) return;
      this.$router.push({
        name: "candidate-ai-interview-session",
        query: {
          inviteId: String(this.candidateInvite?.id || ""),
          jobId: String(this.candidateInvite?.job_id || "")
        }
      });
    },
    formatDateWithOrdinal(this: any, dateIso: string): string {
      const d = new Date(dateIso);
      if (isNaN(d.getTime())) return "";
      const day = d.getDate();
      const month = d.toLocaleString("en-US", { month: "short" });
      const year = d.getFullYear();
      const suffix =
        day % 10 === 1 && day % 100 !== 11
          ? "st"
          : day % 10 === 2 && day % 100 !== 12
            ? "nd"
            : day % 10 === 3 && day % 100 !== 13
              ? "rd"
              : "th";
      return `${day}${suffix} ${month}, ${year}`;
    },
    formatTime(this: any, dateIso: string): string {
      const d = new Date(dateIso);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit"
      });
    },
    async resolveJobTitleById(this: any, jobId: number): Promise<string> {
      if (!jobId) return "";
      try {
        const resp = await get_job_by_id(jobId);
        const payload = resp?.data?.data || resp?.data || {};
        return String(payload?.job_title || payload?.title || "").trim();
      } catch (_e) {
        // ignore
      }
      return "";
    }
  }
  ,
  async created(this: any) {
    await this.$store.dispatch("recruiter_ai_interviews/" + INIT_AI_INTERVIEW_INVITES);
    const candidateId = Number(this.user?.id || 0);
    const inviteJobId = Number(this.candidateInvite?.job_id || 0);
    if (candidateId) {
      try {
        this.candidateProfile = await this.fetchProfileById(candidateId);
      } catch (_e) {
        this.candidateProfile = null;
      }
      try {
        const reportResp = await get_latest_interview_report(
          candidateId,
          "ai",
          inviteJobId || null
        );
        const report = reportResp?.data?.data || null;
        this.aiInterviewCompleted = Boolean(
          report &&
            (report?.status === "completed" ||
              (report?.ai_report && Object.keys(report.ai_report).length > 0))
        );
      } catch (_e) {
        this.aiInterviewCompleted = false;
      }
    }
    if (!this.candidateInvite) return;

    const dIso = this.candidateInvite.scheduled_at;
    this.resolvedJobTitle = await this.resolveJobTitleById(inviteJobId);
    this.interview = {
      ...this.interview,
      date: dIso,
      formattedDate: this.formatDateWithOrdinal(dIso),
      formattedTime: this.formatTime(dIso),
      role:
        this.resolvedJobTitle ||
        this.candidateInvite?.job_title ||
        (/^Job\s*#\d+$/i.test(String(this.candidateInvite?.job_role || "").trim())
          ? ""
          : this.candidateInvite?.job_role) ||
        this.candidateInvite?.candidate_role ||
        "Candidate",
      duration:
        this.candidateInvite?.duration_minutes &&
        Number(this.candidateInvite.duration_minutes) > 0
          ? `${Number(this.candidateInvite.duration_minutes)} Minutes`
          : this.interview.duration
    };
  }
});
